import { useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

export const scrollUtilities = {
  scrollToTop: (customElementId = null) => {
    try {
      window.scrollTo(0, 0)
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })

      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0

      const containerSelectors = [
        'main',
        '#root',
        '.app',
        '[role="main"]',
        '.layout',
        '.page-container',
        '.main-container',
        '.app-container',
        '.content-wrapper',
        '.page-wrapper',
        '.main-content',
        '.site-content',
      ]

      containerSelectors.forEach(selector => {
        const element = document.querySelector(selector)
        if (element) {
          element.scrollTop = 0
        }
      })

      if (customElementId) {
        const customElement = document.getElementById(customElementId)
        if (customElement) {
          customElement.scrollTop = 0
        }
      }
    } catch (error) {
      console.warn('Error in scrollToTop:', error)
    }
  },

  setupScrollRestoration: () => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
  },
}

export const useScrollManager = (options = {}) => {
  const {
    customElementId = null,
    forceScrollOnMount = false,
    forceScrollOnRouteChange = true,
    maxScrollDuration = 0,
    scrollCheckInterval = 200,
  } = options

  const location = useLocation()
  const scrollIntervalsRef = useRef([])
  const isScrollingRef = useRef(false)
  const previousPathnameRef = useRef(location.pathname)

  const clearScrollIntervals = useCallback(() => {
    scrollIntervalsRef.current.forEach(interval => clearInterval(interval))
    scrollIntervalsRef.current = []
  }, [])

  const stopScrolling = useCallback(() => {
    isScrollingRef.current = false
    clearScrollIntervals()
  }, [clearScrollIntervals])

  const scrollToTop = useCallback(() => {
    scrollUtilities.scrollToTop(customElementId)
  }, [customElementId])

  const forceScrollToTop = useCallback(() => {
    clearScrollIntervals()

    console.log('Force scroll to top initiated')
    isScrollingRef.current = true

    scrollToTop()

    requestAnimationFrame(() => {
      if (isScrollingRef.current) {
        scrollToTop()
      }
    })

    const timeoutId = setTimeout(() => {
      if (isScrollingRef.current) {
        scrollToTop()
      }
    }, 100)

    scrollIntervalsRef.current.push(timeoutId)

    const interval = setInterval(() => {
      if (isScrollingRef.current) {
        scrollToTop()
      }
    }, scrollCheckInterval)

    scrollIntervalsRef.current.push(interval)

    setTimeout(() => {
      stopScrolling()
    }, maxScrollDuration)
  }, [
    scrollToTop,
    clearScrollIntervals,
    stopScrolling,
    scrollCheckInterval,
    maxScrollDuration,
  ])

  const setupScrollRestoration = useCallback(() => {
    scrollUtilities.setupScrollRestoration()
  }, [])

  useEffect(() => {
    if (
      forceScrollOnRouteChange &&
      previousPathnameRef.current !== location.pathname
    ) {
      setupScrollRestoration()
      forceScrollToTop()
      previousPathnameRef.current = location.pathname
    }
  }, [
    location.pathname,
    forceScrollOnRouteChange,
    setupScrollRestoration,
    forceScrollToTop,
  ])

  useEffect(() => {
    setupScrollRestoration()

    if (forceScrollOnMount) {
      forceScrollToTop()
    }
  }, [forceScrollOnMount, setupScrollRestoration, forceScrollToTop])

  useEffect(() => {
    return () => {
      stopScrolling()
    }
  }, [stopScrolling])

  return {
    scrollToTop,
    forceScrollToTop,
    setupScrollRestoration,
    stopScrolling,
    isScrolling: isScrollingRef.current,
  }
}

export const withScrollManager = (WrappedComponent, scrollOptions = {}) => {
  const ComponentWithScrollManager = props => {
    const scrollManager = useScrollManager(scrollOptions)

    return <WrappedComponent {...props} scrollManager={scrollManager} />
  }

  ComponentWithScrollManager.displayName = `withScrollManager(${
    WrappedComponent.displayName || WrappedComponent.name
  })`

  return ComponentWithScrollManager
}

export const ScrollManager = ({
  children,
  customElementId = null,
  forceScrollOnMount = false,
  forceScrollOnRouteChange = true,
  onScrollStart,
  onScrollEnd,
}) => {
  const scrollManager = useScrollManager({
    customElementId,
    forceScrollOnMount,
    forceScrollOnRouteChange,
  })

  useEffect(() => {
    if (scrollManager.isScrolling && onScrollStart) {
      onScrollStart()
    } else if (!scrollManager.isScrolling && onScrollEnd) {
      onScrollEnd()
    }
  }, [scrollManager.isScrolling, onScrollStart, onScrollEnd])

  return children
}

class LegacyScrollManager {
  forceScrollToTop = (customElementId = null) => {
    scrollUtilities.scrollToTop(customElementId)
  }

  scrollToTop = () => {
    scrollUtilities.scrollToTop()
  }

  setupScrollRestoration = () => {
    scrollUtilities.setupScrollRestoration()
  }
}

export const scrollManager = new LegacyScrollManager()
